import React, { useContext, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import { primero } from '../finanzas-cxp/shared.js';
import CapturaGastosCargosModal from './CapturaGastosCargosModal.jsx';
import { filaContenedor } from './ListadoContenedoresAdminPage.jsx';

const dinero = (n) => `$${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

function TablaSimple({ cabeceras, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-slate-200">
        <thead className="bg-slate-50">
          <tr>{cabeceras.map((h) => <th key={h} className="px-3 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">{h}</th>)}</tr>
        </thead>
        <tbody className="[&>tr:nth-child(even)]:bg-slate-50">{children}</tbody>
      </table>
    </div>
  );
}

function Tarjeta({ titulo, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <div className="px-4 py-3 border-b border-slate-200"><h5 className="font-semibold text-slate-800">{titulo}</h5></div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function BuscarContenedorPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('contenedores:ver');

  const [numero, setNumero] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [contenedor, setContenedor] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [cargandoGastos, setCargandoGastos] = useState(false);
  const [cargandoCargos, setCargandoCargos] = useState(false);

  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [modal, setModal] = useState(null); // 'gastos' | 'cargos'

  useEffect(() => {
    if (!puedeVer) return;
    apiClient.get('/proveedores', { params: { limit: 500 } }).then((r) => setProveedores(r.data.data || [])).catch(() => {});
    apiClient.get('/clientes', { params: { limit: 500 } }).then((r) => setClientes(r.data.data || [])).catch(() => {});
    apiClient.get('/conceptos-gastos', { params: { limit: 500 } }).then((r) => setConceptos(r.data.data || [])).catch(() => {});
  }, [puedeVer]);

  async function cargarGastos(id) {
    setCargandoGastos(true);
    try { const { data } = await apiClient.get(`/gastos-contenedor/contenedor/${id}`); setGastos(data.data || []); }
    catch { setGastos([]); } finally { setCargandoGastos(false); }
  }
  async function cargarCargos(id) {
    setCargandoCargos(true);
    try { const { data } = await apiClient.get(`/cargos-contenedor/contenedor/${id}`); setCargos(data.data || []); }
    catch { setCargos([]); } finally { setCargandoCargos(false); }
  }

  async function buscar() {
    if (!numero.trim()) {
      Swal.fire({ icon: 'warning', title: 'Atención', text: 'Por favor ingrese un número de contenedor' });
      return;
    }
    setBuscando(true); setNoEncontrado(false); setContenedor(null); setGastos([]); setCargos([]);
    try {
      const { data } = await apiClient.get(`/contenedores/numero/${encodeURIComponent(numero.trim())}`);
      if (data.data) {
        const fila = filaContenedor(data.data);
        setContenedor(fila);
        await Promise.all([cargarGastos(fila._id), cargarCargos(fila._id)]);
      }
    } catch (e) {
      if (e.status === 404) setNoEncontrado(true);
      else Swal.fire({ icon: 'error', title: 'Error', text: 'Error al buscar el contenedor. Intente nuevamente.' });
    } finally {
      setBuscando(false);
    }
  }

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900 text-center">Buscar Contenedor</h1>

      <Tarjeta titulo="Búsqueda por Número de Contenedor">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full md:w-1/3">
            <label className="label">Número de Contenedor</label>
            <input
              type="text" className="input-base" placeholder="Ingrese el número de contenedor" value={numero}
              onChange={(e) => setNumero(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
            />
          </div>
          <button type="button" onClick={buscar} disabled={buscando} className="btn-primary">{buscando ? 'Buscando...' : 'Buscar'}</button>
        </div>
      </Tarjeta>

      {noEncontrado && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-4">
          <h4 className="font-semibold mb-1">Contenedor no encontrado</h4>
          <p>No se encontró ningún contenedor con el número: <strong>{numero}</strong></p>
          <hr className="my-2 border-amber-200" />
          <p>Verifique que el número sea correcto e intente nuevamente.</p>
        </div>
      )}

      {contenedor && (
        <>
          <Tarjeta titulo="Información del Contenedor">
            <TablaSimple cabeceras={['Numero', 'Fecha', 'ETA', 'No. Interno', 'Contenedor', 'Naviera', 'Cliente', 'Agente Aduanal', 'Forwarder', 'Puerto', 'Modalidad', 'Asegurado', 'Status', 'Acciones']}>
              <tr>
                <td className="px-3 py-2">{contenedor.numero}</td>
                <td className="px-3 py-2">{contenedor.fecha}</td>
                <td className="px-3 py-2">{contenedor.etaReal}</td>
                <td className="px-3 py-2">{contenedor.numeroInterno}</td>
                <td className="px-3 py-2">{contenedor.contenedor}</td>
                <td className="px-3 py-2">{contenedor.navieras}</td>
                <td className="px-3 py-2">{contenedor.clientes}</td>
                <td className="px-3 py-2">{contenedor.agentes}</td>
                <td className="px-3 py-2">{contenedor.forwarders}</td>
                <td className="px-3 py-2">{contenedor.puerto}</td>
                <td className="px-3 py-2">{contenedor.modalidad}</td>
                <td className="px-3 py-2">{contenedor.asegurado}</td>
                <td className="px-3 py-2">{contenedor.status}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button type="button" className="btn-primary text-xs px-2 py-1 mr-1" onClick={() => setModal('gastos')}>Gastos</button>
                  <button type="button" className="btn-outline text-xs px-2 py-1" onClick={() => setModal('cargos')}>Cargos</button>
                </td>
              </tr>
            </TablaSimple>
          </Tarjeta>

          <Tarjeta titulo="Gastos Existentes">
            {cargandoGastos ? <p className="text-center text-slate-500">Cargando gastos...</p> : gastos.length > 0 ? (
              <TablaSimple cabeceras={['Fecha', 'Proveedor', 'Concepto', 'Descripción', 'Moneda', 'Total', 'Saldo']}>
                {gastos.map((g) => (
                  <tr key={g._id}>
                    <td className="px-3 py-2">{g.fecha}</td>
                    <td className="px-3 py-2">{primero(g.proveedores)?.name || 'N/A'}</td>
                    <td className="px-3 py-2">{primero(g.conceptosGastos)?.name || 'N/A'}</td>
                    <td className="px-3 py-2">{g.descripcion}</td>
                    <td className="px-3 py-2">{g.moneda}</td>
                    <td className="px-3 py-2">{dinero(g.total)}</td>
                    <td className="px-3 py-2">{dinero(g.saldo)}</td>
                  </tr>
                ))}
              </TablaSimple>
            ) : (
              <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-800 p-3">No hay gastos registrados para este contenedor.</div>
            )}
          </Tarjeta>

          <Tarjeta titulo="Cargos Existentes">
            {cargandoCargos ? <p className="text-center text-slate-500">Cargando cargos...</p> : cargos.length > 0 ? (
              <TablaSimple cabeceras={['Fecha', 'Concepto', 'Descripción', 'Moneda', 'Total', 'Saldo']}>
                {cargos.map((c) => (
                  <tr key={c._id}>
                    <td className="px-3 py-2">{c.fecha}</td>
                    <td className="px-3 py-2">{primero(c.conceptosGastos)?.name || 'N/A'}</td>
                    <td className="px-3 py-2">{c.descripcion}</td>
                    <td className="px-3 py-2">{c.moneda}</td>
                    <td className="px-3 py-2">{dinero(c.total)}</td>
                    <td className="px-3 py-2">{dinero(c.saldo)}</td>
                  </tr>
                ))}
              </TablaSimple>
            ) : (
              <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-800 p-3">No hay cargos registrados para este contenedor.</div>
            )}
          </Tarjeta>
        </>
      )}

      <CapturaGastosCargosModal
        open={!!modal} onClose={() => setModal(null)} tipo={modal} contenedor={contenedor}
        proveedores={proveedores} conceptos={conceptos} clientes={clientes}
        onGuardado={() => { if (contenedor) { cargarGastos(contenedor._id); cargarCargos(contenedor._id); } }}
      />
    </div>
  );
}
